<template>
  <div class="budget-settings-section">
    <div class="reward-budget-container">
      <div class="label">
        Set a reward budget (Optional)
        <small>Help manage spending by setting a budget limit for this award.</small>
      </div>
      <app-input
        :value="amount"
        :validate-immediate="false"
        name="Reward budget amount"
        type="number"
        step="0.01"
        placeholder="0.00"
        inputmode="decimal"
        :prepend="currencySymbol"
        class="reward-budget-input"
        @input="onAmountInput"
        @keydown="onAmountKeydown"
        @blur="onAmountBlur"
        @change="onAmountBlur"
      >
        <app-input
          :value="cadence"
          type="select"
          :options="cadenceOptions"
          :undefined-allow="true"
          undefined-label="Select period"
          prepend="Per"
          class="reward-budget-cadence"
          :validate-immediate="false"
          @input="$emit('update:cadence', $event)"
        />
      </app-input>
    </div>

    <div v-if="showYearStart" class="reward-budget-year-start-container">
      <div class="label">
        Budget year start
        <small v-if="!yearStartDisabled">When does your budget year begin?</small>
        <small v-else>
          This has been set in a different program and cannot be changed. To update, please reach to your CSM.
        </small>
      </div>
      <div class="reward-budget-year-start-inputs">
        <app-input
          :value="yearStartMonth"
          :validate-immediate="false"
          name="Budget year start month"
          type="select"
          :options="monthOptions"
          :undefined-allow="true"
          undefined-label="Select month"
          :rules="yearStartMonthRules"
          :disabled="yearStartDisabled"
          class="reward-budget-year-start-input mb-0"
          @input="$emit('update:yearStartMonth', $event)"
        />
        <app-input
          :value="yearStartDay"
          :validate-immediate="false"
          name="Budget year start day"
          type="select"
          :options="yearStartDayOptions"
          :undefined-allow="true"
          undefined-label="Select day"
          :rules="yearStartDayRules"
          :disabled="yearStartDisabled || !yearStartMonth"
          class="reward-budget-year-start-input mb-0"
          @input="$emit('update:yearStartDay', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import AppInput from '@/components/AppInput.vue';
import { RECOGNITION_BUDGET_CADENCE_OPTIONS, MONTH_OPTIONS, toTwoDecimalsOrEmpty } from '@/pages/recognition/constants';

const props = defineProps({
  currencySymbol: {
    type: String,
    default: '£',
  },
  amount: {
    type: [String, Number],
    default: '',
  },
  cadence: {
    type: String,
    default: undefined,
  },
  yearStartMonth: {
    type: [String, Number],
    default: undefined,
  },
  yearStartDay: {
    type: [String, Number],
    default: undefined,
  },
  yearStartDayOptions: {
    type: Array,
    default: () => [],
  },
  yearStartDisabled: {
    type: Boolean,
    default: false,
  },
  yearStartMonthRules: {
    type: String,
    default: '',
  },
  yearStartDayRules: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:amount', 'update:cadence', 'update:yearStartMonth', 'update:yearStartDay']);

const cadenceOptions = RECOGNITION_BUDGET_CADENCE_OPTIONS;
const monthOptions = MONTH_OPTIONS;

const showYearStart = computed(() => {
  const quarterly = RECOGNITION_BUDGET_CADENCE_OPTIONS[0].value;
  const yearly = RECOGNITION_BUDGET_CADENCE_OPTIONS[1].value;
  return props.cadence === quarterly || props.cadence === yearly;
});

const onAmountInput = value => {
  const raw = String(value ?? '').replace(/[^0-9.]/g, '');
  const [intPart, decPartRaw] = raw.split('.');
  if (decPartRaw !== undefined) {
    emit('update:amount', `${intPart || '0'}.${decPartRaw.slice(0, 2)}`);
  } else {
    emit('update:amount', intPart || '');
  }
};

const onAmountKeydown = e => {
  const isDigit = /^[0-9]$/.test(e.key);
  const isDot = e.key === '.';
  const isControlKey =
    ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(e.key) || e.ctrlKey || e.metaKey;

  if (isControlKey) {
    return;
  }

  const el = e.target;
  const value = String(el?.value ?? '');
  const selectionStart = el?.selectionStart ?? value.length;
  const selectionEnd = el?.selectionEnd ?? value.length;

  if (isDot && value.includes('.')) {
    e.preventDefault();
    return;
  }

  if (isDigit) {
    const before = value.slice(0, selectionStart);
    const after = value.slice(selectionEnd);
    const next = `${before}${e.key}${after}`;
    const match = next.match(/^\d*(?:\.(\d*))?/);
    const dec = match && match[1] ? match[1] : '';
    if (dec.length > 2) {
      e.preventDefault();
      return;
    }
  }

  if (!isDigit && !isDot) {
    e.preventDefault();
  }
};

const onAmountBlur = () => {
  const val = props.amount;
  emit('update:amount', val === '' ? '' : toTwoDecimalsOrEmpty(val));
};
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';
</style>
